import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import Svg, { Path, Circle, Defs, RadialGradient, Stop } from 'react-native-svg';
import { ConcreteItemType } from '@eduwrite/shared';

interface ItemSvgRendererProps {
    type: ConcreteItemType;
    size?: number;
    color?: string;
    style?: StyleProp<ViewStyle>;
    testID?: string;
}

export const ItemSvgRenderer: React.FC<ItemSvgRendererProps> = ({
    type,
    size = 48,
    style,
    testID = 'item-svg-renderer',
}) => {
    const renderItemContent = () => {
        switch (type) {
            case 'apple':
                return (
                    <Svg width={size} height={size} viewBox="0 0 100 100">
                        <Defs>
                            <RadialGradient id="appleGrad" cx="40" cy="40" r="45" gradientUnits="userSpaceOnUse">
                                <Stop offset="0%" stopColor="#FF8585" />
                                <Stop offset="70%" stopColor="#EF4444" />
                                <Stop offset="100%" stopColor="#B91C1C" />
                            </RadialGradient>
                        </Defs>
                        {/* Stalk */}
                        <Path d="M50 24 C50 12, 60 8, 64 6" stroke="#78350F" strokeWidth="5" strokeLinecap="round" fill="none" />
                        {/* Leaf */}
                        <Path d="M52 18 Q68 14 66 26 Q56 26 52 18 Z" fill="#22C55E" stroke="#15803D" strokeWidth="1.5" />
                        {/* Apple Body */}
                        <Path
                            d="M50 30 C30 20, 10 35, 12 60 C14 82, 34 94, 50 90 C66 94, 86 82, 88 60 C90 35, 70 20, 50 30 Z"
                            fill="url(#appleGrad)"
                            stroke="#991B1B"
                            strokeWidth="2.5"
                        />
                        {/* Specular highlight */}
                        <Circle cx="35" cy="45" r="8" fill="#FFFFFF" opacity={0.4} />
                    </Svg>
                );

            case 'car':
                return (
                    <Svg width={size} height={size} viewBox="0 0 100 100">
                        <Defs>
                            <RadialGradient id="carGrad" cx="45" cy="35" r="45" gradientUnits="userSpaceOnUse">
                                <Stop offset="0%" stopColor="#60A5FA" />
                                <Stop offset="100%" stopColor="#2563EB" />
                            </RadialGradient>
                        </Defs>
                        {/* Car Body Roof / Cabin */}
                        <Path d="M22 56 L34 34 Q40 30 60 30 L74 34 L82 56 Z" fill="#93C5FD" stroke="#1D4ED8" strokeWidth="2.5" />
                        {/* Car Lower Body */}
                        <Path
                            d="M12 54 Q12 50 18 50 L84 50 Q92 50 92 58 L90 70 Q90 74 84 74 L18 74 Q12 74 12 68 Z"
                            fill="url(#carGrad)"
                            stroke="#1D4ED8"
                            strokeWidth="3"
                        />
                        {/* Wheels */}
                        <Circle cx="30" cy="74" r="12" fill="#1E293B" stroke="#64748B" strokeWidth="2" />
                        <Circle cx="30" cy="74" r="5" fill="#E2E8F0" />
                        <Circle cx="72" cy="74" r="12" fill="#1E293B" stroke="#64748B" strokeWidth="2" />
                        <Circle cx="72" cy="74" r="5" fill="#E2E8F0" />
                        {/* Headlight */}
                        <Circle cx="87" cy="58" r="4" fill="#FDE047" stroke="#CA8A04" strokeWidth="1" />
                    </Svg>
                );

            case 'star':
                return (
                    <Svg width={size} height={size} viewBox="0 0 100 100">
                        <Defs>
                            <RadialGradient id="starGrad" cx="50" cy="45" r="45" gradientUnits="userSpaceOnUse">
                                <Stop offset="0%" stopColor="#FEF08A" />
                                <Stop offset="65%" stopColor="#F59E0B" />
                                <Stop offset="100%" stopColor="#D97706" />
                            </RadialGradient>
                        </Defs>
                        <Path
                            d="M50 10 L62 36 L90 40 L70 60 L75 88 L50 74 L25 88 L30 60 L10 40 L38 36 Z"
                            fill="url(#starGrad)"
                            stroke="#B45309"
                            strokeWidth="3"
                            strokeLinejoin="round"
                        />
                        {/* Shimmer dot */}
                        <Circle cx="50" cy="40" r="5" fill="#FFFFFF" opacity={0.6} />
                    </Svg>
                );

            case 'frog':
                return (
                    <Svg width={size} height={size} viewBox="0 0 100 100">
                        <Defs>
                            <RadialGradient id="frogGrad" cx="50" cy="50" r="40" gradientUnits="userSpaceOnUse">
                                <Stop offset="0%" stopColor="#86EFAC" />
                                <Stop offset="75%" stopColor="#22C55E" />
                                <Stop offset="100%" stopColor="#15803D" />
                            </RadialGradient>
                        </Defs>
                        {/* Eye bumps */}
                        <Circle cx="32" cy="34" r="14" fill="#22C55E" stroke="#15803D" strokeWidth="2" />
                        <Circle cx="68" cy="34" r="14" fill="#22C55E" stroke="#15803D" strokeWidth="2" />
                        {/* Frog Body */}
                        <Path
                            d="M20 54 C20 40, 80 40, 80 54 C82 72, 70 86, 50 86 C30 86, 18 72, 20 54 Z"
                            fill="url(#frogGrad)"
                            stroke="#15803D"
                            strokeWidth="2.5"
                        />
                        {/* Eye Pupils */}
                        <Circle cx="32" cy="32" r="6" fill="#000000" />
                        <Circle cx="34" cy="30" r="2.5" fill="#FFFFFF" />
                        <Circle cx="68" cy="32" r="6" fill="#000000" />
                        <Circle cx="70" cy="30" r="2.5" fill="#FFFFFF" />
                        {/* Cheeks */}
                        <Circle cx="28" cy="60" r="4" fill="#F472B6" opacity={0.6} />
                        <Circle cx="72" cy="60" r="4" fill="#F472B6" opacity={0.6} />
                        {/* Smile */}
                        <Path d="M40 64 Q50 72 60 64" stroke="#14532D" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                    </Svg>
                );

            case 'bear':
                return (
                    <Svg width={size} height={size} viewBox="0 0 100 100">
                        <Defs>
                            <RadialGradient id="bearGrad" cx="50" cy="45" r="40" gradientUnits="userSpaceOnUse">
                                <Stop offset="0%" stopColor="#FDBA74" />
                                <Stop offset="75%" stopColor="#EA580C" />
                                <Stop offset="100%" stopColor="#C2410C" />
                            </RadialGradient>
                        </Defs>
                        {/* Ears */}
                        <Circle cx="26" cy="28" r="12" fill="#EA580C" stroke="#C2410C" strokeWidth="2" />
                        <Circle cx="26" cy="28" r="6" fill="#FDBA74" />
                        <Circle cx="74" cy="28" r="12" fill="#EA580C" stroke="#C2410C" strokeWidth="2" />
                        <Circle cx="74" cy="28" r="6" fill="#FDBA74" />
                        {/* Body */}
                        <Circle cx="50" cy="58" r="32" fill="url(#bearGrad)" stroke="#C2410C" strokeWidth="2.5" />
                        {/* Snout */}
                        <Circle cx="50" cy="62" r="12" fill="#FED7AA" />
                        <Circle cx="50" cy="58" r="4" fill="#431407" />
                        {/* Eyes */}
                        <Circle cx="38" cy="48" r="4" fill="#000000" />
                        <Circle cx="62" cy="48" r="4" fill="#000000" />
                    </Svg>
                );

            case 'counter':
            default:
                return (
                    <Svg width={size} height={size} viewBox="0 0 60 60">
                        <Defs>
                            <RadialGradient id="counterGrad" cx="24" cy="24" r="28" gradientUnits="userSpaceOnUse">
                                <Stop offset="0%" stopColor="#FF8080" />
                                <Stop offset="70%" stopColor="#EF4444" />
                                <Stop offset="100%" stopColor="#DC2626" />
                            </RadialGradient>
                        </Defs>
                        <Circle cx="30" cy="30" r="27" fill="#FCA5A5" opacity={0.6} />
                        <Circle cx="30" cy="30" r="24" fill="#DC2626" />
                        <Circle cx="30" cy="29" r="20" fill="url(#counterGrad)" />
                        <Circle cx="30" cy="29" r="14" fill="none" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="1.5" />
                    </Svg>
                );
        }
    };

    return (
        <View testID={testID} style={[styles.container, { width: size, height: size }, style]}>
            {renderItemContent()}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
        elevation: 3,
    },
});
